// PerformancePage.js  —  FX Capital India  |  Performance Dashboard
// READ-ONLY page. Never writes to any database.
// All calculations arrive as ready-to-display values from the API.
//
// FIX 1: token now comes as a prop from App.js (same as DashboardPage) —
//        localStorage/sessionStorage kept only as fallback.
// FIX 2: every field name now matches the deployed PerformanceController
//        JSON exactly (fcAmount, realisationDate, costGl, marketClose, etc.)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CONFIG from '../config';
const API_BASE = CONFIG.API;

// ── Colour palette matching the existing app ──────────────────────────────────
const C = {
  green:   '#00c853',
  red:     '#ff1744',
  amber:   '#ffab00',
  blue:    '#2196f3',
  purple:  '#9c27b0',
  teal:    '#009688',
  dark:    '#121212',
  surface: '#1e1e1e',
  card:    '#252525',
  border:  '#333333',
  muted:   '#888888',
  text:    '#e0e0e0',
  dim:     '#b0b0b0',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n, d = 2) =>
  n == null ? 'N/A' : Number(n).toLocaleString('en-IN', {
    minimumFractionDigits: d, maximumFractionDigits: d
  });

const fmtCr = (n) => {
  if (n == null) return 'N/A';
  const abs = Math.abs(n);
  const sign = n < 0 ? '−' : '+';
  if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(2)} Cr`;
  if (abs >= 100000)   return `${sign}₹${(abs / 100000).toFixed(2)} L`;
  return `${sign}₹${abs.toFixed(0)}`;
};

const glColor = (n) => {
  if (n == null) return C.muted;
  return n >= 0 ? C.green : C.red;
};

// Grade labels exactly as the backend sends them
const gradeColor = (g) => ({
  'Excellent':     C.green,
  'Good':          '#64dd17',
  'Average':       C.amber,
  'Below Average': '#ff6d00',
  'Poor':          C.red,
}[g] || C.muted);

// ── Date range helpers ────────────────────────────────────────────────────────
const toIso = (d) => d.toISOString().slice(0, 10);

const getDateRange = (period) => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const d = today.getDate();
  switch (period) {
    case 'MTD':  return { from: toIso(new Date(y, m, 1)),    to: toIso(today) };
    case 'QTD':  {
      const qStart = new Date(y, Math.floor(m / 3) * 3, 1);
      return { from: toIso(qStart), to: toIso(today) };
    }
    case 'YTD':  return { from: toIso(new Date(y, 3, 1)),    to: toIso(today) }; // April 1
    case '12M':  return { from: toIso(new Date(y - 1, m, d)), to: toIso(today) };
    default:     return { from: toIso(new Date(y, m, 1)),    to: toIso(today) };
  }
};

// ── Rate position ruler ───────────────────────────────────────────────────────
const RulerBar = ({ low, cost, customs, realised, sma, high }) => {
  if (!low && !high) return <div style={{ color: C.muted, fontSize: 12 }}>Rate history data required for this view</div>;

  const range = (high || 0) - (low || 0);
  const pct = (v) => range > 0 && v != null ? `${((v - low) / range * 100).toFixed(1)}%` : null;

  const markers = [
    { label: 'Low',      value: low,      color: C.muted,  shape: '◆' },
    { label: 'Cost',     value: cost,     color: C.blue,   shape: '▲' },
    { label: 'Customs',  value: customs,  color: C.purple, shape: '▲' },
    { label: 'SMA',      value: sma,      color: C.amber,  shape: '●' },
    { label: 'Realised', value: realised, color: C.green,  shape: '★' },
    { label: 'High',     value: high,     color: C.muted,  shape: '◆' },
  ].filter(m => m.value != null && m.value > 0);

  return (
    <div style={{ padding: '12px 0' }}>
      <div style={{
        position: 'relative', height: 8, background: '#333',
        borderRadius: 4, margin: '32px 0 16px'
      }}>
        {realised != null && realised > 0 && (
          <div style={{
            position: 'absolute', left: 0, width: pct(realised) || 0,
            height: '100%', background: `linear-gradient(90deg, ${C.red}, ${C.green})`,
            borderRadius: 4
          }} />
        )}
        {markers.map(m => (
          <div key={m.label} style={{
            position: 'absolute', left: pct(m.value),
            transform: 'translateX(-50%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center'
          }}>
            <div style={{
              fontSize: 16, color: m.color, marginTop: -24, lineHeight: 1
            }}>{m.shape}</div>
            <div style={{
              fontSize: 9, color: m.color, marginTop: 12,
              whiteSpace: 'nowrap', fontWeight: 600
            }}>{m.label}</div>
            <div style={{ fontSize: 9, color: C.dim }}>{fmt(m.value, 2)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Stacked bar for settlement mix ────────────────────────────────────────────
const SettlementBar = ({ spot, forward, pcfc }) => {
  const total = (spot || 0) + (forward || 0) + (pcfc || 0);
  if (total === 0) return null;
  return (
    <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', marginBottom: 8 }}>
      <div style={{ width: `${(spot || 0) / total * 100}%`, background: C.blue }} />
      <div style={{ width: `${(forward || 0) / total * 100}%`, background: C.green }} />
      <div style={{ width: `${(pcfc || 0) / total * 100}%`, background: C.purple }} />
    </div>
  );
};

// ── Rate sparkline (SVG polyline) ─────────────────────────────────────────────
// FIX: backend /rate-series returns a PLAIN ARRAY of {date, close} rows
const RateChart = ({ series, realisedRate, costRate }) => {
  if (!Array.isArray(series) || series.length === 0)
    return <div style={{ color: C.muted, fontSize: 12, padding: 24 }}>No rate history data available for this period</div>;

  const w = 680, h = 160, pad = { t: 16, r: 16, b: 24, l: 48 };
  const iw = w - pad.l - pad.r;
  const ih = h - pad.t - pad.b;

  const closes = series.map(r => r.close);
  const minV = Math.min(...closes) * 0.9995;
  const maxV = Math.max(...closes) * 1.0005;
  const rangeV = maxV - minV || 1;

  const xScale = (i) => pad.l + (i / Math.max(series.length - 1, 1)) * iw;
  const yScale = (v) => pad.t + ih - ((v - minV) / rangeV) * ih;

  const points = series.map((r, i) => `${xScale(i)},${yScale(r.close)}`).join(' ');

  const hLine = (v, color, label, dash = '') => {
    if (v == null || v <= 0 || v < minV || v > maxV) return null;
    const y = yScale(v);
    return (
      <g key={label}>
        <line x1={pad.l} y1={y} x2={w - pad.r} y2={y}
          stroke={color} strokeWidth={1} strokeDasharray={dash} opacity={0.7} />
        <text x={pad.l - 4} y={y + 4} textAnchor="end" fill={color}
          fontSize={9}>{label}</text>
      </g>
    );
  };

  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', maxWidth: w, display: 'block' }}>
      {[0, 0.25, 0.5, 0.75, 1].map(t => (
        <line key={t} x1={pad.l} y1={pad.t + ih * t} x2={w - pad.r} y2={pad.t + ih * t}
          stroke="#2a2a2a" strokeWidth={1} />
      ))}
      <polyline points={points} fill="none" stroke={C.muted} strokeWidth={1.5} />
      {hLine(realisedRate, C.green, 'Realised', '')}
      {hLine(costRate, C.blue, 'Cost', '4 2')}
      {[0, Math.floor(series.length / 2), series.length - 1].map(i => (
        <text key={i} x={xScale(i)} y={h - 4} textAnchor="middle"
          fill={C.muted} fontSize={9}>{series[i]?.date?.slice(5)}</text>
      ))}
    </svg>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function PerformancePage({ token: tokenProp }) {
  const defaultRange = getDateRange('MTD');

  // ── Filter state ─────────────────────────────────────────────────────────
  const [period,   setPeriod]   = useState('MTD');
  const [currency, setCurrency] = useState('USD');
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate,   setToDate]   = useState(defaultRange.to);

  // ── Data state ────────────────────────────────────────────────────────────
  const [summary,     setSummary]     = useState(null);
  const [mix,         setMix]         = useState(null);
  const [rateSeries,  setRateSeries]  = useState(null);
  const [bills,       setBills]       = useState(null);
  const [customers,   setCustomers]   = useState(null);
  const [forwards,    setForwards]    = useState(null);
  const [pcfc,        setPcfc]        = useState(null);
  const [ohlcStatus,  setOhlcStatus]  = useState(null);

  // ── Loading / error state ─────────────────────────────────────────────────
  const [loading,  setLoading]  = useState(false);
  const [errors,   setErrors]   = useState({});

  // ── Sort state for bills table ────────────────────────────────────────────
  const [billSort, setBillSort] = useState('date');

  // ── Active tabs ───────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('bills');

  // ── Build query params ────────────────────────────────────────────────────
  const qs = useCallback(() =>
    `pair=${currency}&from=${fromDate}&to=${toDate}`,
    [currency, fromDate, toDate]
  );

  // FIX 1: token comes from App.js as a prop — same pattern as DashboardPage.
  // Storage lookups are only a fallback in case the prop is not passed.
  const getToken = () =>
    tokenProp ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token') ||
    '';

  const apiFetch = async (endpoint) => {
    const res = await fetch(`${API_BASE}/api/bills/performance/${endpoint}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    if (!res.ok) throw new Error(`${endpoint.split('?')[0]}: HTTP ${res.status}`);
    return res.json();
  };

  // ── Fetch all sections in parallel ───────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setErrors({});
    const errs = {};

    const safe = async (key, fn) => {
      try {
        return await fn();
      } catch (e) {
        errs[key] = e.message;
        return null;
      }
    };

    const [s, m, rs, b, c, f, p, o] = await Promise.all([
      safe('summary',    () => apiFetch(`summary?${qs()}`)),
      safe('mix',        () => apiFetch(`settlement-mix?${qs()}`)),
      safe('rateSeries', () => apiFetch(`rate-series?${qs()}`)),
      safe('bills',      () => apiFetch(`bills?${qs()}`)),
      safe('customers',  () => apiFetch(`customers?${qs()}`)),
      safe('forwards',   () => apiFetch(`forwards?${qs()}`)),
      safe('pcfc',       () => apiFetch(`pcfc?${qs()}`)),
      safe('ohlcStatus', () => apiFetch(`ohlc-status?pair=${currency}`)),
    ]);

    setSummary(s);
    setMix(m);
    setRateSeries(rs);
    setBills(b);
    setCustomers(c);
    setForwards(f);
    setPcfc(p);
    setOhlcStatus(o);
    setErrors(errs);
    setLoading(false);
  }, [qs]);

  // ── Update dates when period tab changes ──────────────────────────────────
  const handlePeriod = (p) => {
    setPeriod(p);
    if (p !== 'Custom') {
      const r = getDateRange(p);
      setFromDate(r.from);
      setToDate(r.to);
    }
  };

  useEffect(() => { fetchAll(); }, [fromDate, toDate, currency]);

  // ── Client-side sorting of bills (backend returns newest-first) ───────────
  const sortedBills = useMemo(() => {
    if (!Array.isArray(bills)) return bills;
    const arr = [...bills];
    if (billSort === 'fc')          arr.sort((a, b) => (b.fcAmount || 0) - (a.fcAmount || 0));
    else if (billSort === 'costgl') arr.sort((a, b) => (b.costGl || 0) - (a.costGl || 0));
    else arr.sort((a, b) => (b.realisationDate || '').localeCompare(a.realisationDate || ''));
    return arr;
  }, [bills, billSort]);

  // ── Settlement mix rows built from the backend's flat response ────────────
  // Backend sends: { spotFc, spotAvg, spotPct, fwdFc, fwdAvg, fwdPct,
  //                  pcfcFc, pcfcAvg, pcfcPct, totalFc }
  const mixRows = useMemo(() => {
    if (!mix) return [];
    const cost = summary?.wtdAvgCostRate || 0;
    const pl = (fc, rate) => (cost > 0 && rate > 0 && fc > 0) ? (rate - cost) * fc : null;
    return [
      { method: 'Spot',    fc: mix.spotFc,  pct: mix.spotPct,  rate: mix.spotAvg,  plVsCost: pl(mix.spotFc, mix.spotAvg),  color: C.blue },
      { method: 'Forward', fc: mix.fwdFc,   pct: mix.fwdPct,   rate: mix.fwdAvg,   plVsCost: pl(mix.fwdFc, mix.fwdAvg),    color: C.green },
      { method: 'PCFC',    fc: mix.pcfcFc,  pct: mix.pcfcPct,  rate: mix.pcfcAvg,  plVsCost: pl(mix.pcfcFc, mix.pcfcAvg),  color: C.purple },
      { method: 'Total',   fc: mix.totalFc, pct: mix.totalFc > 0 ? 100 : 0, rate: null, plVsCost: null, color: C.text, bold: true },
    ];
  }, [mix, summary]);

  // ── Styles ────────────────────────────────────────────────────────────────
  const s = {
    page:    { background: C.dark, minHeight: '100vh', padding: '0 0 80px', color: C.text, fontFamily: 'Inter, system-ui, sans-serif' },
    header:  { background: C.surface, borderBottom: `1px solid ${C.border}`, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    section: { margin: '0 12px 12px', background: C.card, borderRadius: 10, padding: 14, border: `1px solid ${C.border}` },
    label:   { fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 },
    value:   { fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 12 },
    kpiCard: { background: C.surface, borderRadius: 8, padding: 12, border: `1px solid ${C.border}` },
    h2:      { fontSize: 13, fontWeight: 600, color: C.dim, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 },
    tab:     (active) => ({
               padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
               background: active ? '#2196f3' : 'transparent',
               color: active ? '#fff' : C.muted,
               border: `1px solid ${active ? '#2196f3' : C.border}`,
               cursor: 'pointer', whiteSpace: 'nowrap'
             }),
    periodTabs: { display: 'flex', gap: 6, overflowX: 'auto', padding: '10px 12px', background: C.surface },
    currencyBtn: (active) => ({
                  padding: '4px 12px', borderRadius: 16, fontSize: 12,
                  background: active ? '#ff6f00' : 'transparent',
                  color: active ? '#fff' : C.muted,
                  border: `1px solid ${active ? '#ff6f00' : C.border}`,
                  cursor: 'pointer'
                }),
    table:   { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
    th:      { textAlign: 'right', padding: '6px 8px', color: C.muted, fontSize: 10, borderBottom: `1px solid ${C.border}`, fontWeight: 600 },
    thL:     { textAlign: 'left',  padding: '6px 8px', color: C.muted, fontSize: 10, borderBottom: `1px solid ${C.border}`, fontWeight: 600 },
    td:      { textAlign: 'right', padding: '7px 8px', borderBottom: `1px solid #2a2a2a`, fontSize: 12 },
    tdL:     { textAlign: 'left',  padding: '7px 8px', borderBottom: `1px solid #2a2a2a`, fontSize: 12 },
    badge:   (g) => ({
               display: 'inline-block', padding: '2px 8px', borderRadius: 12,
               fontSize: 10, fontWeight: 700,
               background: gradeColor(g) + '33',
               color: gradeColor(g)
             }),
    na:      { color: C.muted, fontSize: 11 },
  };

  const ErrorBanner = ({ msg }) =>
    msg ? <div style={{ background: '#b71c1c22', color: '#ef9a9a', fontSize: 11, padding: '6px 10px', borderRadius: 6, marginBottom: 8 }}>⚠ {msg}</div> : null;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>

      {/* ── HEADER ─────────────────────────────────────────────────────── */}
      <div style={s.header}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Performance</div>
          <div style={{ fontSize: 11, color: C.muted }}>Settled bill analytics</div>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          style={{
            background: 'transparent', border: `1px solid ${C.border}`,
            color: C.dim, borderRadius: 8, padding: '6px 14px',
            fontSize: 12, cursor: 'pointer'
          }}
        >
          {loading ? '⟳ Loading…' : '⟳ Refresh'}
        </button>
      </div>

      {/* ── PERIOD TABS ────────────────────────────────────────────────── */}
      <div style={s.periodTabs}>
        {['MTD', 'QTD', 'YTD', '12M', 'Custom'].map(p => (
          <button key={p} style={s.tab(period === p)} onClick={() => handlePeriod(p)}>{p}</button>
        ))}
        <div style={{ flex: 1 }} />
        {['USD', 'EUR', 'GBP', 'AED'].map(c => (
          <button key={c} style={s.currencyBtn(currency === c)} onClick={() => setCurrency(c)}>{c}</button>
        ))}
      </div>

      {/* Custom date inputs */}
      {period === 'Custom' && (
        <div style={{ display: 'flex', gap: 8, padding: '8px 12px', background: C.surface, borderBottom: `1px solid ${C.border}` }}>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, padding: '4px 8px', fontSize: 12 }} />
          <span style={{ color: C.muted, lineHeight: '28px' }}>→</span>
          <input type="date" value={toDate}   onChange={e => setToDate(e.target.value)}
            style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, padding: '4px 8px', fontSize: 12 }} />
        </div>
      )}

      {/* ── KPI CARDS ──────────────────────────────────────────────────── */}
      <div style={{ padding: '10px 12px 0' }}>
        <ErrorBanner msg={errors.summary} />
        {summary && (
          <>
            <div style={s.kpiGrid}>
              {/* FC Settled */}
              <div style={s.kpiCard}>
                <div style={s.label}>FC Settled</div>
                <div style={{ ...s.value, color: C.text }}>{fmt(summary.totalFc, 0)}</div>
                <div style={{ fontSize: 10, color: C.muted }}>{currency} | {summary.billsCount} bills</div>
              </div>
              {/* Realised Rate */}
              <div style={s.kpiCard}>
                <div style={s.label}>Avg Realised Rate</div>
                <div style={{ ...s.value, color: C.green }}>{fmt(summary.wtdAvgRealisedRate, 4)}</div>
                <div style={{ fontSize: 10, color: C.muted }}>vs cost {fmt(summary.wtdAvgCostRate, 4)}</div>
              </div>
              {/* Market High */}
              <div style={s.kpiCard}>
                <div style={s.label}>Period High</div>
                <div style={{ ...s.value, color: summary.marketHigh ? C.amber : C.muted }}>
                  {summary.marketHigh ? fmt(summary.marketHigh, 4) : 'N/A'}
                </div>
                <div style={{ fontSize: 10, color: C.muted }}>
                  Low: {summary.marketLow ? fmt(summary.marketLow, 4) : 'N/A'}
                </div>
              </div>
              {/* Performance Grade */}
              <div style={s.kpiCard}>
                <div style={s.label}>Performance Grade</div>
                <div style={{ ...s.value, color: gradeColor(summary.perfGrade), fontSize: 17 }}>
                  {summary.perfGrade}
                </div>
                <div style={{ fontSize: 10, color: C.muted }}>
                  {summary.perfPosition != null ? `Top ${(100 - summary.perfPosition).toFixed(0)}% of range` : 'Needs OHLC data'}
                </div>
              </div>
            </div>

            {/* Market SMA */}
            <div style={{ ...s.kpiCard, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={s.label}>Period SMA (Market Average)</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.amber }}>
                  {summary.marketSma ? fmt(summary.marketSma, 4) : 'N/A'}
                </div>
              </div>
              {summary.wtdAvgRealisedRate > 0 && summary.marketSma && (
                <div style={{ textAlign: 'right' }}>
                  <div style={s.label}>vs SMA</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: glColor(summary.wtdAvgRealisedRate - summary.marketSma) }}>
                    {summary.wtdAvgRealisedRate >= summary.marketSma ? '▲' : '▼'} {Math.abs(summary.wtdAvgRealisedRate - summary.marketSma).toFixed(4)}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── RATE CHART ─────────────────────────────────────────────────── */}
      <div style={{ ...s.section, marginTop: 10 }}>
        <div style={s.h2}>Rate History Chart</div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 10 }}>
          <span style={{ color: C.muted }}>── Daily Close</span>
          <span style={{ color: C.green }}>── Avg Realised</span>
          <span style={{ color: C.blue }}>-- Cost Rate</span>
        </div>
        <ErrorBanner msg={errors.rateSeries} />
        <RateChart
          series={rateSeries}
          realisedRate={summary?.wtdAvgRealisedRate}
          costRate={summary?.wtdAvgCostRate}
        />
      </div>

      {/* ── RATE RULER ─────────────────────────────────────────────────── */}
      {summary?.marketHigh && (
        <div style={s.section}>
          <div style={s.h2}>Rate Position — Period Range</div>
          <RulerBar
            low={summary.marketLow}
            cost={summary.wtdAvgCostRate}
            customs={null}
            realised={summary.wtdAvgRealisedRate}
            sma={summary.marketSma}
            high={summary.marketHigh}
          />
        </div>
      )}

      {/* ── SETTLEMENT MIX ─────────────────────────────────────────────── */}
      <div style={s.section}>
        <div style={s.h2}>Settlement Mix</div>
        <ErrorBanner msg={errors.mix} />
        {mix && (
          <>
            <SettlementBar
              spot={mix.spotFc}
              forward={mix.fwdFc}
              pcfc={mix.pcfcFc}
            />
            <div style={{ display: 'flex', gap: 6, fontSize: 10, marginBottom: 10 }}>
              <span style={{ color: C.blue }}>■ Spot</span>
              <span style={{ color: C.green }}>■ Forward</span>
              <span style={{ color: C.purple }}>■ PCFC</span>
            </div>
            {mixRows.map(row => (
              <div key={row.method} style={{
                display: 'flex', justifyContent: 'space-between',
                padding: '6px 0', borderBottom: row.method !== 'Total' ? `1px solid ${C.border}` : 'none',
                fontWeight: row.bold ? 700 : 400
              }}>
                <div style={{ color: row.color, fontSize: 12 }}>{row.method}</div>
                <div style={{ display: 'flex', gap: 16, fontSize: 11, color: C.dim }}>
                  <span>{fmt(row.fc, 0)}</span>
                  <span style={{ color: C.muted }}>{fmt(row.pct, 1)}%</span>
                  <span style={{ color: row.color }}>{row.rate != null ? fmt(row.rate, 4) : ''}</span>
                  <span style={{ color: glColor(row.plVsCost) }}>
                    {row.plVsCost != null ? fmtCr(row.plVsCost) : ''}
                  </span>
                </div>
              </div>
            ))}
            <div style={{ fontSize: 9, color: C.muted, marginTop: 6 }}>
              Columns: FC amount · % of total · avg rate · P&L vs weighted cost rate
            </div>
          </>
        )}
      </div>

      {/* ── SECTION TABS ───────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 6, padding: '0 12px 10px', overflowX: 'auto' }}>
        {[
          { key: 'bills',     label: `Bills (${bills?.length ?? 0})` },
          { key: 'customers', label: `Customers (${customers?.length ?? 0})` },
          { key: 'forwards',  label: `Forwards (${forwards?.length ?? 0})` },
          { key: 'pcfc',      label: `PCFC (${pcfc?.length ?? 0})` },
        ].map(t => (
          <button key={t.key} style={s.tab(activeTab === t.key)} onClick={() => setActiveTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {/* ── BILLS TABLE ────────────────────────────────────────────────── */}
      {activeTab === 'bills' && (
        <div style={s.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={s.h2}>Bill-by-Bill G/L</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {[['date', 'Date'], ['fc', 'FC'], ['costgl', 'Cost G/L']].map(([v, l]) => (
                <button key={v} style={s.currencyBtn(billSort === v)} onClick={() => setBillSort(v)}>{l}</button>
              ))}
            </div>
          </div>
          <ErrorBanner msg={errors.bills} />
          {sortedBills?.length === 0 && (
            <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: 24 }}>
              No settled bills in this period
            </div>
          )}
          {sortedBills?.length > 0 && (
            <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.thL}>Invoice</th>
                    <th style={s.thL}>Customer</th>
                    <th style={s.th}>FC Settled</th>
                    <th style={s.th}>Date</th>
                    <th style={s.th}>Realised</th>
                    <th style={s.th}>Cost G/L</th>
                    <th style={s.th}>Acctg G/L</th>
                    <th style={s.th}>Opp G/L</th>
                    <th style={s.th}>Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedBills.map((b, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#1a1a1a' }}>
                      <td style={s.tdL}>{b.invoiceNo}</td>
                      <td style={s.tdL}>{b.customer}</td>
                      <td style={s.td}>{fmt(b.fcAmount, 0)}</td>
                      <td style={s.td}>{b.realisationDate?.slice(0, 10)}</td>
                      <td style={s.td}>{fmt(b.realisedRate, 4)}</td>
                      <td style={{ ...s.td, color: glColor(b.costGl), fontWeight: 600 }}>{fmtCr(b.costGl)}</td>
                      <td style={{ ...s.td, color: glColor(b.acctGl) }}>{fmtCr(b.acctGl)}</td>
                      <td style={{ ...s.td, color: b.oppGl == null ? C.muted : glColor(b.oppGl) }}>
                        {b.oppGl == null ? <span style={s.na}>N/A</span> : fmtCr(b.oppGl)}
                      </td>
                      <td style={s.td}>
                        {b.marketGrade && b.marketGrade !== 'N/A'
                          ? <span style={s.badge(b.marketGrade)}>{b.marketGrade}</span>
                          : <span style={s.na}>N/A</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── CUSTOMERS TABLE ────────────────────────────────────────────── */}
      {activeTab === 'customers' && (
        <div style={s.section}>
          <div style={s.h2}>Customer Performance</div>
          <ErrorBanner msg={errors.customers} />
          {customers?.length === 0 && (
            <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: 24 }}>
              No customers with settled bills in this period
            </div>
          )}
          {customers?.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.thL}>Customer</th>
                    <th style={s.th}>Bills</th>
                    <th style={s.th}>Total FC</th>
                    <th style={s.th}>Avg Rate</th>
                    <th style={s.th}>Avg Cost</th>
                    <th style={s.th}>Cost G/L</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#1a1a1a' }}>
                      <td style={s.tdL}>{c.customer}</td>
                      <td style={s.td}>{c.billCount}</td>
                      <td style={s.td}>{fmt(c.totalFc, 0)}</td>
                      <td style={s.td}>{fmt(c.avgRealised, 4)}</td>
                      <td style={s.td}>{fmt(c.avgCost, 4)}</td>
                      <td style={{ ...s.td, color: glColor(c.totalCostGl), fontWeight: 600 }}>{fmtCr(c.totalCostGl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── FORWARDS TABLE ─────────────────────────────────────────────── */}
      {activeTab === 'forwards' && (
        <div style={s.section}>
          <div style={s.h2}>Forward Contract P&amp;L</div>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 10 }}>
            Premium = (Booked Rate − Spot at Booking) × FC. P&amp;L vs Market = (Booked Rate − Market Close) × FC.
            Negative P&amp;L vs market means market moved in your favour — the forward still removed your risk.
          </div>
          <ErrorBanner msg={errors.forwards} />
          {forwards?.length === 0 && (
            <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: 24 }}>
              No forward contracts settled in this period
            </div>
          )}
          {forwards?.length > 0 && (
            <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.thL}>Deal No</th>
                    <th style={s.thL}>Bank</th>
                    <th style={s.th}>FC Used</th>
                    <th style={s.th}>Booked</th>
                    <th style={s.th}>Spot@Book</th>
                    <th style={s.th}>Mkt Close</th>
                    <th style={s.th}>Premium</th>
                    <th style={s.th}>vs Market</th>
                    <th style={s.thL}>Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {forwards.map((f, i) => {
                    const verdict = f.forwardPl == null ? 'N/A'
                      : f.forwardPl >= 0 ? 'Forward beat market' : 'Market beat forward';
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#1a1a1a' }}>
                        <td style={s.tdL}>{f.dealNo}</td>
                        <td style={s.tdL}>{f.bank}</td>
                        <td style={s.td}>{fmt(f.fcUsed, 0)}</td>
                        <td style={s.td}>{fmt(f.bookedRate, 4)}</td>
                        <td style={s.td}>{fmt(f.spotAtBooking, 4)}</td>
                        <td style={s.td}>
                          {f.marketClose != null ? fmt(f.marketClose, 4) : <span style={s.na}>N/A</span>}
                        </td>
                        <td style={{ ...s.td, color: glColor(f.premiumCaptured) }}>{fmtCr(f.premiumCaptured)}</td>
                        <td style={{ ...s.td, color: f.forwardPl == null ? C.muted : glColor(f.forwardPl) }}>
                          {f.forwardPl != null ? fmtCr(f.forwardPl) : <span style={s.na}>N/A</span>}
                        </td>
                        <td style={{ ...s.tdL, color: verdict === 'Forward beat market' ? C.green : C.amber, fontSize: 11 }}>
                          {verdict}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── PCFC TABLE ─────────────────────────────────────────────────── */}
      {activeTab === 'pcfc' && (
        <div style={s.section}>
          <div style={s.h2}>PCFC Repayment P&amp;L</div>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 10 }}>
            P&amp;L vs Draw Rate = (Draw Rate − Realised Rate) × FC Repaid.
            Positive = PCFC was drawn at a higher rate than the invoice settled — the PCFC was beneficial.
          </div>
          <ErrorBanner msg={errors.pcfc} />
          {pcfc?.length === 0 && (
            <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: 24 }}>
              No PCFC repayments in this period
            </div>
          )}
          {pcfc?.length > 0 && (
            <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.thL}>Bank</th>
                    <th style={s.th}>FC Repaid</th>
                    <th style={s.th}>Draw Rate</th>
                    <th style={s.th}>Realised</th>
                    <th style={s.th}>Rate Diff</th>
                    <th style={s.th}>P&amp;L vs Draw</th>
                    <th style={s.th}>Mkt Close</th>
                    <th style={s.th}>P&amp;L vs Mkt</th>
                    <th style={s.th}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {pcfc.map((p, i) => {
                    const rateDiff = (p.drawRate != null && p.realisedRate != null)
                      ? p.drawRate - p.realisedRate : null;
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#1a1a1a' }}>
                        <td style={s.tdL}>{p.bank}</td>
                        <td style={s.td}>{fmt(p.fcRepaid, 0)}</td>
                        <td style={s.td}>{fmt(p.drawRate, 4)}</td>
                        <td style={s.td}>{fmt(p.realisedRate, 4)}</td>
                        <td style={{ ...s.td, color: glColor(rateDiff) }}>{rateDiff != null ? fmt(rateDiff, 4) : 'N/A'}</td>
                        <td style={{ ...s.td, color: glColor(p.pcfcPl), fontWeight: 600 }}>{fmtCr(p.pcfcPl)}</td>
                        <td style={s.td}>
                          {p.marketClose != null ? fmt(p.marketClose, 4) : <span style={s.na}>N/A</span>}
                        </td>
                        <td style={{ ...s.td, color: p.oppVsMarket == null ? C.muted : glColor(p.oppVsMarket) }}>
                          {p.oppVsMarket != null ? fmtCr(p.oppVsMarket) : <span style={s.na}>N/A</span>}
                        </td>
                        <td style={s.td}>{p.repaymentDate?.slice(0, 10)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── OHLC STATUS ────────────────────────────────────────────────── */}
      <div style={s.section}>
        <div style={s.h2}>Rate History Status</div>
        <ErrorBanner msg={errors.ohlcStatus} />
        {ohlcStatus && (
          <div style={{ fontSize: 12 }}>
            <div style={{
              padding: '8px 12px', borderRadius: 6, marginBottom: 8,
              background: ohlcStatus.hasData ? '#00c85311' : '#ff174411',
              color: ohlcStatus.hasData ? C.green : C.red,
              border: `1px solid ${ohlcStatus.hasData ? C.green : C.red}33`
            }}>
              {ohlcStatus.hasData
                ? `✓ ${ohlcStatus.pair}: ${ohlcStatus.rowCount} days of OHLC data — ${ohlcStatus.earliestDate} to ${ohlcStatus.latestDate}`
                : `✕ No OHLC data uploaded for ${ohlcStatus.pair} — opportunity G/L and grades will show N/A. Upload rates in the Rates tab.`}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}